import * as Yup from 'yup';
import AllForm from '../Stateless/Form';
import PrivilegeMatrix from '../Stateless/PrivilegeMatrix';
import { useAtom } from 'jotai';
import { subAdminAtom } from './SubAdmin/SubAdminTable';
import { UseCreateSubAdmin, useEditSubAdmin } from '../Query/AddSubAdminQuery';
import { CircularProgress, Backdrop, Button, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { COLORS } from '../../theme/themeConstants';
import { useNavigate } from 'react-router-dom';
import { useThemeColors } from '../../hooks/useThemeColors';
import { FieldInputProps, FormikProps } from 'formik';
import PhoneNumberInput from './Utils/PhoneNumberInput';
import { useState, useMemo, useEffect } from 'react';
import { moduleActions } from '../Stateless/permissionConfig';
import { splitPhoneNumber } from '../HelperFunctions/validationUtils';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please enter Name ')
    .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces')
    .max(100, 'Name cannot exceed 50 characters')
    .test(
      'not-only-spaces',
      'Name cannot be only spaces',
      (value) => {
        if (typeof value !== 'string') return false;
        return value.trim().length > 0;
      }
    ),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
      'Please enter a valid email address'
    )
    .required('Email is required'),
  mobile: Yup.string()
     .required('Please enter the mobile number')
     .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
     .test(
       'len',
       'Mobile number must be 9 digits or more',
       (val) => val?.length >= 9
     )
     .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  permission: Yup.object().test(
    'valid-permissions',
    function (value) {
      // At least one permission
      if (!value || typeof value !== 'object' || !('permissions' in value)) {
        return this.createError({ message: 'Please select at least one permission' });
      }
      const permissions = value.permissions;
      if (!permissions || typeof permissions !== 'object') {
        return this.createError({ message: 'Please select at least one permission' });
      }
      // At least one permission selected
      const hasAny = Object.values(permissions).some((module) =>
        Object.values(module).some((perm) => perm)
      );
      if (!hasAny) {
        return this.createError({ message: 'Please select at least one permission' });
      }

      // "requires View permission" logic
      for (const [module, modulePerms] of Object.entries(permissions)) {
        // Only check if View is an available action for this module according to moduleActions
        const availableActions = moduleActions[module] || [];
        const hasViewAction = availableActions.includes('View');
        
        if (hasViewAction) {
          const hasView = modulePerms['View'];
          const dependentActionSelected = Object.entries(modulePerms).some(
            ([action, selected]) =>
              action !== 'View' && 
              availableActions.includes(action) && 
              selected
          );
          if (dependentActionSelected && !hasView) {
            return this.createError({
              message: `${module}: Other actions require View permission`,
            });
          }
        }
      }
      return true;
    }
  ),
});

export interface SubAdminData {
  id: string;
  name: string;
  email: string;
  mobileNumber: string;
  lastLoginDate: string;
  lastLogoutDate: string;
  lastLoginIp: string;
  status: string;
  privilege: boolean;
  permissions: any;
  serialNumber: number;
}

// Add initial privileges structure
const initialPrivileges = {
  privilege: false,
  permissions: {
    category: { View: false, Add: false, Edit: false, Delete: false },
    choices: { View: false, Add: false, Edit: false, Delete: false },
    merchant: { View: false, Add: false, Edit: false, Delete: false },
    restaurant: { View: false, Add: false, Edit: false, Delete: false },
    customer: { View: false, Add: false, Edit: false, Delete: false },
    item: { View: false, Add: false, Edit: false, Delete: false },
    'Delivery Manager': { View: false, Add: false, Edit: false, Delete: false },
    'Delivery Boy': { View: false, Add: false, Edit: false, Delete: false },
    CMS: { View: false, Add: false, Edit: false, Delete: false },
    FAQ: { View: false, Add: false, Edit: false, Delete: false },
    Review: { View: false, Delete: false },
    Order: { View: false },
    Commission: { View: false },
    Inventory: { View: false },
    Cancellation: { View: false },
    'Refer Friend': { View: false },
    'News Letter': { View: false },
    'Featured Restaurant': { View: false },
    'Failed Orders': { View: false },
    'Delivery Boy Map': { View: false, Add: false, Edit: false, Delete: false },
    Reports: { View: false },
    'Delivery Commission': { View: false },
    'Promotional And Offers': {
      View: false,
      Add: false,
      Edit: false,
      Delete: false,
    },
  },
};

// Helper to normalize permissions to only allowed lowercase keys
const normalizePermissions = (permissions: any) => {
  const result: any = {};
  Object.keys(moduleActions).forEach((mod) => {
    result[mod] = {};
    (moduleActions[mod] || []).forEach((act) => {
      const lower = act.toLowerCase();
      let value = undefined;
      if (permissions?.[mod]) {
        // Support various casings plus API-preferred "unBlock"
        const camel = act.charAt(0).toLowerCase() + act.slice(1);
        const apiPreferred = act === 'Unblock' ? 'unBlock' : undefined;
        value =
          permissions[mod][lower] ??
          (apiPreferred ? permissions[mod][apiPreferred] : undefined) ??
          permissions[mod][act] ??
          permissions[mod][camel] ??
          false;
      } else {
        value = false;
      }
      result[mod][act] = value;
    });
  });
  return result;
};

const EditAddSubAdmin = ({ onCancel }: { onCancel: () => void }) => {
  const { warning } = useThemeColors();
  const navigate = useNavigate();
  const [subAdminData] = useAtom(subAdminAtom);

  // console.log("subAdminData", subAdminData)

  const { mutate: CreateSubAdmin, isPending: CreateSubAdminLoading } =
    UseCreateSubAdmin();

  const { mutate: EditSubAdmin, isPending: EditSubAdminLoading } =
    useEditSubAdmin();

  // const fullNumber = subAdminData?.mobileNumber;
  // const countryCode = fullNumber?.slice(0, -10);

  const {countryCode, phone} = splitPhoneNumber(subAdminData?.mobileNumber)
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode || '+962');
  
  // Update country code when subAdminData changes
  useEffect(() => {
    if (subAdminData?.mobileNumber) {
      // const fullNumber = subAdminData.mobileNumber;
      // const code = fullNumber?.slice(0, -10) || '+962';
      const {countryCode: code} = splitPhoneNumber(subAdminData.mobileNumber)
      setSelectedCountryCode(code);
    }
  }, [subAdminData]);

  // Get initial privileges from subAdminData if it exists, otherwise use default
  const initialPrivilegeValues = useMemo(() => subAdminData
    ? {
      privilege: subAdminData.privilege || false,
      permissions: normalizePermissions(subAdminData.permissions),
    }
    : initialPrivileges, [subAdminData]);

  const AddSubAdminForm = AllForm({
    initialValues: {
      name: subAdminData?.name || '',
      email: subAdminData?.email || '',
      mobile: phone || '',
      permission: initialPrivilegeValues,
    },
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: async (values: any) => {
      const permissions = values.permission.permissions;
      const lowercasedPermissions: { [key: string]: { [key: string]: boolean } } =
        {};
      Object.keys(permissions).forEach((module) => {
        lowercasedPermissions[module] = {};
        Object.keys(permissions[module]).forEach((action) => {
          // Map display action names to API keys.
          // API expects "unBlock" (camel B) instead of plain lowercase "unblock".
          const apiKey = action === 'Unblock' || action === 'unblock' ? 'unBlock' : action.toLowerCase();
          lowercasedPermissions[module][apiKey] = permissions[module][action];
        });
      });
      const modifiedValues = {
        name: values.name,
        email: values.email,
        // mobileNumber: `+${values.mobile.replace(/^\+/, '')}`,
        mobileNumber: `${selectedCountryCode}${values.mobile}`,
        privilege: values.permission.privilege,
        permissions: lowercasedPermissions,
      };
      try {
        if (subAdminData) {
          EditSubAdmin(
            {
              id: subAdminData.id || '',
              data: modifiedValues,
            },
            {
              onSuccess: () => {
                onCancel();
                // closeForm();
                // resetForm();
              },
              onError: (error: any) => {
                console.log(error);
                // setErrors(parseJavaError(error));
              },
            }
          );
        } else {
          CreateSubAdmin(modifiedValues, {
            onSuccess: () => {
              navigate('/sub-admin/list');
              onCancel();
              // closeForm();
              // resetForm();
            },
            onError: (error: any) => {
              console.log(error);
              // setErrors(parseJavaError(error));
            },
          });
        }
      } catch (error) {
        console.log(error);
        // setErrors(parseJavaError(error));
      }
    },

    fields: [
      {
        name: 'name',
        label: 'Name:',
        type: 'text',
        isRequired: true,
      },
      {
        name: 'email',
        label: 'Email:',
        type: 'text',
        isRequired: true,
      },
      {
        name: 'mobile',
        label: 'Mobile No:',
        type: 'custom',
        isRequired: true,
        component: ({
          field,
          form,
        }: {
          field: FieldInputProps<any>;
          form: FormikProps<any>;
        }) => (
          <PhoneNumberInput
            field={field}
            form={form}
            selectedCountryCode={selectedCountryCode}
            onCountryCodeChange={setSelectedCountryCode}
          />
        )
      },
      {
        name: 'permission',
        type: 'customSelect',
        component: (props: any) => (
          <PrivilegeMatrix
            {...props}
            error={props.form.errors.permission}
            isTouched={props.form.touched.permission}
          />
        ),
        value: initialPrivilegeValues,
      },
    ],

    customButton: (formikProps: any) => (
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* <Button
          type="submit"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            backgroundColor: 'rgb(76, 10, 39)',
            '&:hover': {
              backgroundColor: 'rgb(76, 10, 39, 0.8)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
          onClick={() => formikProps.handleSubmit()}
        >
          {subAdminData ? 'Update' : 'Create'}
        </Button> */}
        <Button
          type="submit"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            backgroundColor: COLORS.primary.main,
            '&:hover': {
              backgroundColor: alpha(COLORS.primary.main, 0.8),
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
          onClick={(e) => {
            e.preventDefault();
            // Mark all fields as touched to show validation errors
            Object.keys(formikProps.values).forEach((fieldName) => {
              formikProps.setFieldTouched(fieldName, true, false);
            });
            // Check if there are validation errors
            if (Object.keys(formikProps.errors).length === 0) {
              formikProps.handleSubmit();
            } else {
              console.log('Validation failed:', formikProps.errors);
            }
          }}
          disabled={EditSubAdminLoading}
        >
          {EditSubAdminLoading ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              {subAdminData ? 'Updating...' : 'Creating...'}
            </>
          ) : subAdminData ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>

        {!subAdminData && (
          <Button
            type="button"
            variant="outlined"
            onClick={() => formikProps.resetForm()}
            sx={{
              textTransform: 'none',
              fontSize: 14,
              px: 3,
              py: 1,
              borderColor: '#666',
              color: '#666',
              '&:hover': {
                borderColor: '#333',
                color: '#333',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            Reset
          </Button>
        )}
        <Button
          type="button"
          variant="outlined"
          color="error"
          onClick={onCancel}
          sx={{
            bgcolor: warning.main,
            color: '#fff',
            '&:hover': {
              bgcolor: warning.dark,
              color: '#fff',
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    ),
  });

  return (
    <>
      <Backdrop open={CreateSubAdminLoading} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <AddSubAdminForm />
    </>
  );
};

export default EditAddSubAdmin;
